import { useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { track } from '@vercel/analytics';

/**
 * Hook for tracking wallet connections to analytics platforms
 * Tracks wallet provider information when users connect their wallets
 * Only tracks if user has consented to analytics
 */
export const useWalletTracking = () => {
  const { connected, wallet } = useWallet();
  const { hasConsent, preferences } = useCookieConsent();

  useEffect(() => {
    // Only track if wallet is connected, adapter exists, and analytics consent is granted
    if (!connected || !wallet?.adapter || !hasConsent || !preferences.analytics) {
      return;
    }

    // Extract wallet name from adapter
    const walletName = wallet.adapter?.name || 'Unknown';

    // Check if Vercel Analytics is opted out
    const vercelOptOut = typeof window !== 'undefined' 
      ? localStorage.getItem('vercel-analytics-opt-out') === 'true'
      : false;

    try {
      // Track to Google Analytics
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'wallet_connected', {
          event_category: 'Wallet',
          event_label: walletName,
          wallet_provider: walletName,
          value: 1,
        });
      }

      // Track to Vercel Analytics (only if not opted out)
      if (!vercelOptOut) {
        track('wallet_connected', {
          provider: walletName,
        });
      }
    } catch (error) {
      // Silently fail - don't interrupt user experience
      console.debug('Failed to track wallet connection:', error);
    }
  }, [connected, wallet, hasConsent, preferences.analytics]);
};

